import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import SuccessToast from '../../components/ui/SuccessToast';


const RegisterPage = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [scrollY, setScrollY] = useState(0);
  const [focusedField, setFocusedField] = useState(null);
  const [isMobile, setIsMobile] = useState(false);
  const { signUp, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();

  const [touched, setTouched] = useState({});
  const [step, setStep] = useState(1);
  const [shakeForm, setShakeForm] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrollY(window.scrollY);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (!loading && isAuthenticated) {
      navigate('/nord-pass-subscription-portal', { replace: true });
    }
  }, [isAuthenticated, loading, navigate]);

  const validateFirstName = (v) => !v?.trim() ? 'Fornavn er påkrevd.' : '';
  const validateLastName = (v) => !v?.trim() ? 'Etternavn er påkrevd.' : '';
  const validateEmail = (v) => {
    if (!v?.trim()) return 'E-postadresse er påkrevd.';
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(v)) return 'Ugyldig e-postformat.';
    return '';
  };
  const validatePassword = (v) => {
    if (!v) return 'Passord er påkrevd.';
    if (v?.length < 8) return 'Passordet må være minst 8 tegn.';
    return '';
  };
  const validateConfirmPassword = (v) => {
    if (!v) return 'Bekreft passordet ditt.';
    if (v !== password) return 'Passordene stemmer ikke overens.';
    return '';
  };

  const getFieldError = (field) => {
    if (!touched?.[field]) return '';
    if (field === 'firstName') return validateFirstName(firstName);
    if (field === 'lastName') return validateLastName(lastName);
    if (field === 'email') return validateEmail(email);
    if (field === 'password') return validatePassword(password);
    if (field === 'confirmPassword') return validateConfirmPassword(confirmPassword);
    return '';
  };

  const isFieldValid = (field) => touched?.[field] && getFieldError(field) === '';
  const handleBlur = (field) => setTouched((prev) => ({ ...prev, [field]: true }));

  const passwordStrength = (pwd) => {
    if (!pwd) return { score: 0, label: '', color: 'transparent', bars: 0 };
    let score = 0;
    if (pwd?.length >= 8) score++;
    if (/[A-Z]/?.test(pwd)) score++;
    if (/[a-z]/?.test(pwd)) score++;
    if (/[0-9]/?.test(pwd)) score++;
    if (/[^A-Za-z0-9]/?.test(pwd)) score++;
    const levels = [
    { score: 0, label: '', color: 'transparent', bars: 0 },
    { score: 1, label: 'Svak', color: '#E85555', bars: 1 },
    { score: 2, label: 'Svak', color: '#E85555', bars: 1 },
    { score: 3, label: 'Middels', color: '#F5A623', bars: 2 },
    { score: 4, label: 'God', color: '#4A90E2', bars: 3 },
    { score: 5, label: 'Sterk', color: '#4CAF50', bars: 4 }];

    return levels?.[score] || levels?.[0];
  };

  const pwdStrength = passwordStrength(password);

  const pwdRequirements = [
  { label: 'Minst 8 tegn', met: password?.length >= 8 },
  { label: 'Stor bokstav (A–Z)', met: /[A-Z]/?.test(password) },
  { label: 'Liten bokstav (a–z)', met: /[a-z]/?.test(password) },
  { label: 'Tall (0–9)', met: /[0-9]/?.test(password) },
  { label: 'Spesialtegn (!@#...)', met: /[^A-Za-z0-9]/?.test(password) }];


  const handleNextStep = (e) => {
    e?.preventDefault();
    setError('');
    const fields = ['firstName', 'lastName', 'email'];
    const newTouched = {};
    fields?.forEach((f) => {newTouched[f] = true;});
    setTouched((prev) => ({ ...prev, ...newTouched }));

    if (validateFirstName(firstName) || validateLastName(lastName) || validateEmail(email)) {
      setShakeForm(true);
      setTimeout(() => setShakeForm(false), 600);
      return;
    }
    setStep(2);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setError('');
    const fields = ['password', 'confirmPassword'];
    const newTouched = {};
    fields?.forEach((f) => {newTouched[f] = true;});
    setTouched((prev) => ({ ...prev, ...newTouched }));

    if (validatePassword(password) || validateConfirmPassword(confirmPassword)) {
      setShakeForm(true);
      setTimeout(() => setShakeForm(false), 600);
      return;
    }

    setIsLoading(true);
    try {
      const { error: signUpError } = await signUp(email, password, {
        first_name: firstName?.trim(),
        last_name: lastName?.trim(),
        phone: phone?.trim()
      });
      if (signUpError) {
        setError(signUpError?.message || 'Registrering mislyktes. Prøv igjen.');
        setShakeForm(true);
        setTimeout(() => setShakeForm(false), 600);
      } else {
        setSuccess(true);
        setTimeout(() => navigate('/nord-pass-subscription-portal', { replace: true }), 2000);
      }
    } finally {
      setIsLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#2F353B', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Icon name="Loader2" size={32} color="#F5A623" />
      </div>);

  }

  const inputStyle = (fieldName) => {
    const err = getFieldError(fieldName);
    const valid = isFieldValid(fieldName);
    const isFocused = focusedField === fieldName;
    const borderColor = err ?
    'rgba(232,85,85,0.5)' :
    valid ?
    'rgba(46,196,160,0.5)' :
    isFocused ?
    'rgba(46,196,160,0.5)' :
    'rgba(245,166,35,0.25)';
    const borderBottomColor = err ?
    '#E85555' :
    valid ?
    '#2EC4A0' :
    isFocused ?
    '#2EC4A0' : 'rgba(245,166,35,0.5)';
    return {
      width: '100%',
      padding: isMobile ? '14px 16px' : '13px 16px',
      paddingRight: touched?.[fieldName] ? '40px' : '16px',
      background: isFocused ? 'rgba(46,196,160,0.05)' : 'rgba(15,17,23,0.75)',
      border: '1px solid ' + borderColor,
      borderBottom: '2px solid ' + borderBottomColor,
      color: '#EEF0F2',
      fontFamily: 'Inter, system-ui, -apple-system, sans-serif',
      fontSize: isMobile ? '1rem' : '0.9375rem',
      outline: 'none',
      boxSizing: 'border-box',
      borderRadius: '0',
      transition: 'all 280ms ease, transform 360ms cubic-bezier(0.34, 1.56, 0.64, 1), box-shadow 360ms cubic-bezier(0.34, 1.56, 0.64, 1)',
      letterSpacing: '0.01em',
      transform: isFocused && !isMobile ? 'translateY(-2px) scale(1.008)' : 'translateY(0) scale(1)',
      boxShadow: isFocused ?
      `0 4px 16px rgba(46,196,160,0.15), 0 0 0 2px rgba(46,196,160,0.12), inset 0 1px 3px rgba(0,0,0,0.2)` :
      'inset 0 1px 3px rgba(0,0,0,0.3)'
    };
  };

  const labelStyle = {
    fontFamily: 'Syne, sans-serif',
    fontSize: '0.62rem',
    color: 'rgba(245,166,35,0.85)',
    display: 'block',
    marginBottom: '7px',
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    fontWeight: 700
  };

  const FieldError = ({ field }) => {
    const err = getFieldError(field);
    if (!err) return null;
    return (
      <div className="error-msg-bounce" style={{ display: 'flex', alignItems: 'center', gap: '5px', marginTop: '5px' }}>
        <Icon name="AlertCircle" size={11} color="#E85555" />
        <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.72rem', color: '#E85555' }}>{err}</span>
      </div>);

  };

  const FieldValidIcon = ({ field }) => {
    if (!touched?.[field]) return null;
    const err = getFieldError(field);
    return (
      <div style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
        <Icon name={err ? 'XCircle' : 'CheckCircle'} size={14} color={err ? '#E85555' : '#4CAF50'} />
      </div>);

  };

  return (
    <>
      <Helmet>
        <title>Opprett konto — NordPass</title>
      </Helmet>
      <style>{`
        @keyframes tickIn {
          from { transform: translateY(-50%) scale(0) rotate(-10deg); opacity: 0; }
          to   { transform: translateY(-50%) scale(1) rotate(0deg); opacity: 1; }
        }
        @keyframes shakeX {
          0%, 100% { transform: translateX(0); }
          15%       { transform: translateX(-8px); }
          30%       { transform: translateX(7px); }
          45%       { transform: translateX(-6px); }
          60%       { transform: translateX(5px); }
          75%       { transform: translateX(-3px); }
          90%       { transform: translateX(2px); }
        }
        @keyframes shakeXDesktop {
          0%, 100% { transform: rotate(-0.4deg) translateX(0); }
          15%       { transform: rotate(-0.4deg) translateX(-8px); }
          30%       { transform: rotate(-0.4deg) translateX(7px); }
          45%       { transform: rotate(-0.4deg) translateX(-6px); }
          60%       { transform: rotate(-0.4deg) translateX(5px); }
          75%       { transform: rotate(-0.4deg) translateX(-3px); }
          90%       { transform: rotate(-0.4deg) translateX(2px); }
        }
        @keyframes successPop {
          0%   { transform: scale(0.8); opacity: 0; }
          60%  { transform: scale(1.04); opacity: 1; }
          100% { transform: scale(1); opacity: 1; }
        }
        @keyframes successPopDesktop {
          0%   { transform: scale(0.8) rotate(-0.4deg); opacity: 0; }
          60%  { transform: scale(1.04) rotate(-0.4deg); opacity: 1; }
          100% { transform: scale(1) rotate(-0.4deg); opacity: 1; }
        }
        @keyframes checkDraw {
          from { stroke-dashoffset: 60; opacity: 0; }
          to   { stroke-dashoffset: 0; opacity: 1; }
        }
        @keyframes successFadeIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes errorBounceIn {
          0%   { opacity: 0; transform: translateY(-6px) scale(0.92); }
          55%  { opacity: 1; transform: translateY(2px) scale(1.02); }
          80%  { transform: translateY(-1px) scale(0.99); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes stepSlideIn {
          from { opacity: 0; transform: translateX(20px); }
          to   { opacity: 1; transform: translateX(0); }
        }
        .form-card-shake { animation: shakeX 0.55s cubic-bezier(0.36,0.07,0.19,0.97) both !important; }
        .form-card-shake-desktop { animation: shakeXDesktop 0.55s cubic-bezier(0.36,0.07,0.19,0.97) both !important; }
        .form-card-success { animation: successPop 0.5s cubic-bezier(0.22,1,0.36,1) both !important; }
        .form-card-success-desktop { animation: successPopDesktop 0.5s cubic-bezier(0.22,1,0.36,1) both !important; }
        .error-msg-bounce { animation: errorBounceIn 380ms cubic-bezier(0.34, 1.56, 0.64, 1) both; }
        .step-slide-in { animation: stepSlideIn 300ms cubic-bezier(0.22,1,0.36,1) both; }
      `}</style>
      <div style={{
        minHeight: '100vh',
        background: '#2F353B',
        position: 'relative',
        overflow: 'hidden',
        display: 'flex',
        alignItems: isMobile ? 'flex-start' : 'stretch',
        flexDirection: isMobile ? 'column' : 'row'
      }}>

        {/* Background photo */}
        <div style={{ position: 'absolute', inset: 0, transform: `translateY(${scrollY * 0.2}px)`, willChange: 'transform' }}>
          <img
            src="https://images.unsplash.com/photo-1703356189125-97d4a023e589"
            alt="Norwegian mountain landscape with snow-capped peaks and serene valley"
            style={{ width: '100%', height: '115%', objectFit: 'cover', objectPosition: 'center 30%', filter: 'brightness(0.28) contrast(1.3) saturate(0.6)' }} />
          
        </div>

        {/* Grain texture */}
        <div style={{
          position: 'absolute', inset: 0,
          backgroundImage: 'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 256 256\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noise\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' stitchTiles=\'stitch\'/%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noise)\' opacity=\'0.04\'/%3E%3C/svg%3E")',
          opacity: 0.55, pointerEvents: 'none', zIndex: 2
        }} />

        {/* Decorative blocks - desktop only */}
        {!isMobile &&
        <>
            <div style={{
            position: 'absolute', bottom: 0, right: 0,
            width: '45%', height: '35%',
            background: '#2EC4A0',
            clipPath: 'polygon(100% 40%, 100% 100%, 0 100%)',
            opacity: 0.07, zIndex: 1
          }} />
            <div style={{
            position: 'absolute', top: 0, left: 0,
            width: '35%', height: '25%',
            background: '#F5A623',
            clipPath: 'polygon(0 0, 80% 0, 0 100%)',
            opacity: 0.07, zIndex: 1
          }} />
          </>
        }

        {/* Left vertical sidebar - desktop only */}
        {!isMobile &&
        <div style={{
          position: 'absolute', left: 0, top: 0, bottom: 0,
          width: '48px',
          background: 'rgba(46,196,160,0.04)',
          borderRight: '1px solid rgba(46,196,160,0.12)',
          zIndex: 10,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center'
        }}>
            <div style={{
            writingMode: 'vertical-rl', transform: 'rotate(180deg)',
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: '0.55rem', letterSpacing: '0.35em',
            color: 'rgba(46,196,160,0.5)', textTransform: 'uppercase', whiteSpace: 'nowrap'
          }}>NordPass — Registrering — 2026</div>
            <div style={{ width: '1px', height: '50px', background: 'rgba(46,196,160,0.25)', margin: '14px 0' }} />
            <div style={{ fontSize: '0.5rem', color: 'rgba(46,196,160,0.35)', fontFamily: 'monospace', letterSpacing: '0.1em' }}>R</div>
          </div>
        }

        {/* Ghost watermark - desktop only */}
        {!isMobile &&
        <div style={{
          position: 'absolute', bottom: '-2%', right: '3%',
          fontFamily: 'Impact, sans-serif',
          fontSize: 'clamp(120px, 18vw, 260px)',
          color: 'rgba(46,196,160,0.03)',
          lineHeight: 1, userSelect: 'none', pointerEvents: 'none',
          zIndex: 1, letterSpacing: '-0.05em'
        }}>02</div>
        }

        {/* ── LEFT EDITORIAL PANEL — desktop only ── */}
        {!isMobile &&
        <div style={{
          position: 'relative', zIndex: 10,
          flex: '1 1 auto',
          display: 'flex', flexDirection: 'column', justifyContent: 'center',
          paddingLeft: '80px', paddingRight: '5%',
          paddingTop: '60px', paddingBottom: '60px',
          maxWidth: '55%'
        }}>
            {/* Stamp */}
            <div style={{
            display: 'inline-block', alignSelf: 'flex-start',
            border: '2px solid rgba(46,196,160,0.5)',
            padding: '5px 12px',
            transform: 'rotate(-1.5deg)',
            fontFamily: 'Georgia, serif', fontStyle: 'italic',
            fontSize: '0.6rem', color: 'rgba(46,196,160,0.7)',
            letterSpacing: '0.06em', whiteSpace: 'nowrap',
            marginBottom: '32px'
          }}>ny bruker ✦</div>

            {/* Logo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '28px' }}>
              <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="46" height="46">
                <defs>
                  <linearGradient id="lgSkyR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#0D1929" />
                    <stop offset="100%" stopColor="#1A2E52" />
                  </linearGradient>
                  <linearGradient id="lgFjordR" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#1A3A6B" />
                    <stop offset="100%" stopColor="#0D1929" />
                  </linearGradient>
                  <linearGradient id="lgRoadR" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#4A90E2" />
                    <stop offset="100%" stopColor="#F5A623" />
                  </linearGradient>
                </defs>
                <rect width="48" height="48" rx="6" fill="url(#lgSkyR)" />
                <path d="M0 32 L6 18 L12 26 L18 12 L24 22 L30 8 L36 20 L42 14 L48 24 L48 48 L0 48 Z" fill="url(#lgFjordR)" opacity="0.9" />
                <rect x="0" y="36" width="48" height="12" fill="#0A1520" opacity="0.8" />
                <path d="M24 48 L14 36 L34 36 Z" fill="url(#lgRoadR)" opacity="0.85" />
                <text x="19" y="31" fontFamily="sans-serif" fontSize="10" fontWeight="900" fill="#EEF0F2" letterSpacing="-0.5" opacity="0.95">N</text>
              </svg>
              <span style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.4rem', color: '#EEF0F2', letterSpacing: '-0.04em' }}>NORDPASS</span>
            </div>

            {/* Big editorial headline */}
            <div style={{ marginBottom: '20px' }}>
              <div style={{
              fontFamily: 'Impact, sans-serif',
              fontSize: 'clamp(52px, 7vw, 96px)',
              lineHeight: 0.88,
              color: '#EEF0F2',
              letterSpacing: '-0.02em',
              textTransform: 'uppercase',
              marginBottom: '4px'
            }}>OPPRETT</div>
              <div style={{
              fontFamily: 'Impact, sans-serif',
              fontSize: 'clamp(52px, 7vw, 96px)',
              lineHeight: 0.88,
              color: '#2EC4A0',
              letterSpacing: '-0.02em',
              textTransform: 'uppercase'
            }}>KONTO</div>
            </div>

            {/* Italic subline */}
            <p style={{
            fontFamily: 'Georgia, serif',
            fontStyle: 'italic',
            fontSize: 'clamp(0.9rem, 1.4vw, 1.15rem)',
            color: 'rgba(238,240,242,0.55)',
            lineHeight: 1.6,
            maxWidth: '380px',
            marginBottom: '40px'
          }}>Bli en del av Norges smarteste veifelleskapet og spar tid og penger.</p>

            {/* Decorative rule */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '32px' }}>
              <div style={{ width: '40px', height: '2px', background: '#2EC4A0' }} />
              <div style={{ width: '8px', height: '8px', background: '#F5A623', transform: 'rotate(45deg)' }} />
              <div style={{ flex: 1, height: '1px', background: 'rgba(238,240,242,0.1)' }} />
            </div>

            {/* Benefits list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
              {[
            { icon: 'MapPin', text: 'Sanntids bomstasjonsoversikt' },
            { icon: 'CreditCard', text: 'Automatisk kostnadsberegning' },
            { icon: 'Bell', text: 'Varsler om vegarbeid og stengninger' }]?.
            map((item) =>
            <div key={item?.text} style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '28px', height: '28px', background: 'rgba(46,196,160,0.12)', border: '1px solid rgba(46,196,160,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <Icon name={item?.icon} size={13} color="#2EC4A0" />
                  </div>
                  <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.85rem', color: 'rgba(238,240,242,0.6)' }}>{item?.text}</span>
                </div>
            )}
            </div>
          </div>
        }

        {/* ── RIGHT FORM PANEL ── */}
        <div style={{
          position: 'relative', zIndex: 10,
          flex: isMobile ? '1 1 auto' : '0 0 auto',
          width: isMobile ? '100%' : 'clamp(360px, 40%, 500px)',
          display: 'flex',
          alignItems: isMobile ? 'flex-start' : 'center',
          justifyContent: 'center',
          padding: isMobile ? '24px 16px 40px' : '40px 40px 40px 20px',
          minHeight: isMobile ? '100vh' : 'auto'
        }}>

          {/* Rotated accents - desktop only */}
          {!isMobile &&
          <>
              <div style={{
              position: 'absolute', top: '8%', right: '6%',
              width: '180px', height: '180px',
              border: '1px solid rgba(46,196,160,0.1)',
              transform: 'rotate(12deg)', pointerEvents: 'none'
            }} />
              <div style={{
              position: 'absolute', bottom: '10%', left: '0%',
              width: '100px', height: '100px',
              border: '1px solid rgba(245,166,35,0.08)',
              transform: 'rotate(-8deg)', pointerEvents: 'none'
            }} />
            </>
          }

          {/* Success Toast */}
          <SuccessToast
            visible={success}
            message="Konto opprettet!"
            subtext="Omdirigerer deg nå..."
            onDismiss={() => {}} />
          

          {/* Form card */}
          <div
            className={
            shakeForm ?
            isMobile ? 'form-card-shake' : 'form-card-shake-desktop' :
            success ?
            isMobile ? 'form-card-success' : 'form-card-success-desktop' :
            ''
            }
            style={{
              width: '100%',
              background: '#1A1F28',
              border: '1px solid rgba(255,255,255,0.06)',
              borderTop: success ? '4px solid #4CAF50' : '4px solid #2EC4A0',
              padding: isMobile ? '24px 20px 24px' : '36px 32px 32px',
              boxShadow: '0 32px 80px rgba(0,0,0,0.7), 0 0 0 1px rgba(46,196,160,0.06)',
              transform: isMobile ? 'none' : 'rotate(-0.4deg)',
              position: 'relative',
              transition: 'border-top-color 400ms ease'
            }}>

            {/* Corner tape - desktop only */}
            {!isMobile &&
            <div style={{
              position: 'absolute', top: '-8px', right: '24px',
              width: '48px', height: '16px',
              background: success ? 'rgba(76,175,80,0.35)' : 'rgba(46,196,160,0.3)',
              transform: 'rotate(1deg)'
            }} />
            }

            {/* Mobile header */}
            {isMobile &&
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '20px' }}>
                <svg viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" width="32" height="32">
                  <defs>
                    <linearGradient id="lgSkyRM" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#0D1929" />
                      <stop offset="100%" stopColor="#1A2E52" />
                    </linearGradient>
                    <linearGradient id="lgFjordRM" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#1A3A6B" />
                      <stop offset="100%" stopColor="#0D1929" />
                    </linearGradient>
                    <linearGradient id="lgRoadRM" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor="#4A90E2" />
                      <stop offset="100%" stopColor="#F5A623" />
                    </linearGradient>
                  </defs>
                  <rect width="48" height="48" rx="6" fill="url(#lgSkyRM)" />
                  <path d="M0 32 L6 18 L12 26 L18 12 L24 22 L30 8 L36 20 L42 14 L48 24 L48 48 L0 48 Z" fill="url(#lgFjordRM)" opacity="0.9" />
                  <rect x="0" y="36" width="48" height="12" fill="#0A1520" opacity="0.8" />
                  <path d="M24 48 L14 36 L34 36 Z" fill="url(#lgRoadRM)" opacity="0.85" />
                  <text x="19" y="31" fontFamily="sans-serif" fontSize="10" fontWeight="900" fill="#EEF0F2" letterSpacing="-0.5" opacity="0.95">N</text>
                </svg>
                <div>
                  <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1.1rem', color: '#EEF0F2', letterSpacing: '-0.04em' }}>NORDPASS</div>
                  <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.65rem', color: 'rgba(46,196,160,0.7)' }}>Opprett ny konto</div>
                </div>
              </div>
            }

            {/* Step indicator */}
            {!success &&
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '24px' }}>
                {[1, 2]?.map((s) =>
              <React.Fragment key={s}>
                    <div style={{
                  width: isMobile ? '28px' : '24px',
                  height: isMobile ? '28px' : '24px',
                  borderRadius: '50%',
                  background: step >= s ? '#2EC4A0' : 'rgba(255,255,255,0.08)',
                  border: step >= s ? 'none' : '1px solid rgba(255,255,255,0.15)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontFamily: 'Syne, sans-serif', fontWeight: 800,
                  fontSize: '0.7rem',
                  color: step >= s ? '#1A1F28' : 'rgba(238,240,242,0.3)',
                  transition: 'all 300ms ease',
                  flexShrink: 0
                }}>{s}</div>
                    {s < 2 &&
                <div style={{
                  flex: 1, height: '1px',
                  background: step > s ? '#2EC4A0' : 'rgba(255,255,255,0.1)',
                  transition: 'background 300ms ease'
                }} />
                }
                  </React.Fragment>
              )}
                <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.7rem', color: 'rgba(238,240,242,0.35)', marginLeft: '4px', whiteSpace: 'nowrap' }}>
                  Steg {step} av 2
                </span>
              </div>
            }

            {/* Success state */}
            {success ?
            <div style={{ textAlign: 'center', padding: '24px 0', animation: 'successFadeIn 0.5s cubic-bezier(0.22,1,0.36,1) both' }}>
                <div style={{ marginBottom: '16px', display: 'flex', justifyContent: 'center' }}>
                  <svg width="64" height="64" viewBox="0 0 64 64" fill="none">
                    <circle cx="32" cy="32" r="30" stroke="rgba(76,175,80,0.2)" strokeWidth="2" />
                    <circle cx="32" cy="32" r="30" stroke="#4CAF50" strokeWidth="2"
                  strokeDasharray="188.5" strokeDashoffset="0"
                  style={{ animation: 'checkDraw 0.6s ease 0.1s both', strokeDashoffset: 188.5 }} />
                  
                    <polyline points="20,32 28,42 44,22" stroke="#4CAF50" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"
                  strokeDasharray="60" strokeDashoffset="0"
                  style={{ animation: 'checkDraw 0.4s ease 0.5s both', strokeDashoffset: 60 }} />
                  
                  </svg>
                </div>
                <div style={{ fontFamily: 'Syne, sans-serif', fontWeight: 800, fontSize: '1rem', color: '#4CAF50', letterSpacing: '0.06em', textTransform: 'uppercase', marginBottom: '6px' }}>Konto opprettet!</div>
                <div style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.78rem', color: 'rgba(238,240,242,0.45)' }}>Omdirigerer deg til dashbordet...</div>
              </div> :
            step === 1 ? (
            /* ── STEP 1: Personal info ── */
            <form onSubmit={handleNextStep} noValidate className="step-slide-in">
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
                  <div>
                    <label style={labelStyle}>Fornavn *</label>
                    <div style={{ position: 'relative' }}>
                      <input
                      type="text"
                      value={firstName}
                      onChange={(e) => setFirstName(e?.target?.value)}
                      onFocus={() => setFocusedField('firstName')}
                      onBlur={() => {handleBlur('firstName');setFocusedField(null);}}
                      placeholder="Lars"
                      style={inputStyle('firstName')} />
                    
                      <FieldValidIcon field="firstName" />
                    </div>
                    <FieldError field="firstName" />
                  </div>
                  <div>
                    <label style={labelStyle}>Etternavn *</label>
                    <div style={{ position: 'relative' }}>
                      <input
                      type="text"
                      value={lastName}
                      onChange={(e) => setLastName(e?.target?.value)}
                      onFocus={() => setFocusedField('lastName')}
                      onBlur={() => {handleBlur('lastName');setFocusedField(null);}}
                      placeholder="Andersen"
                      style={inputStyle('lastName')} />
                    
                      <FieldValidIcon field="lastName" />
                    </div>
                    <FieldError field="lastName" />
                  </div>
                </div>
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>E-postadresse *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e?.target?.value)}
                    onFocus={() => setFocusedField('email')}
                    onBlur={() => {handleBlur('email');setFocusedField(null);}}
                    placeholder="din@epost.no"
                    autoComplete="email"
                    style={inputStyle('email')} />
                  
                    <FieldValidIcon field="email" />
                  </div>
                  <FieldError field="email" />
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>Telefonnummer <span style={{ color: 'rgba(238,240,242,0.3)', fontWeight: 400 }}>(valgfritt)</span></label>
                  <input
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e?.target?.value)}
                  onFocus={() => setFocusedField('phone')}
                  onBlur={() => setFocusedField(null)}
                  placeholder="+47 XXX XX XXX"
                  style={{ ...inputStyle('phone'), paddingRight: '16px' }} />
                
                </div>
                {error &&
              <div style={{ background: 'rgba(232,85,85,0.08)', border: '1px solid rgba(232,85,85,0.25)', borderLeft: '3px solid #E85555', padding: '10px 14px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Icon name="AlertCircle" size={13} color="#E85555" />
                    <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.8rem', color: '#E85555' }}>{error}</span>
                  </div>
              }
                <button
                type="submit"
                style={{
                  width: '100%',
                  padding: isMobile ? '16px 24px' : '15px 24px',
                  background: '#2EC4A0',
                  border: 'none',
                  color: '#1A1F28',
                  fontFamily: 'Syne, sans-serif',
                  fontSize: isMobile ? '0.9375rem' : '0.875rem',
                  fontWeight: 800,
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  boxShadow: '0 4px 24px rgba(46,196,160,0.35)',
                  transition: 'all 200ms ease',
                  WebkitTapHighlightColor: 'transparent',
                  touchAction: 'manipulation'
                }}
                className="btn-spring">
                  <span>Neste steg</span>
                  <Icon name="ArrowRight" size={15} color="#1A1F28" />
                </button>
                <div style={{ marginTop: '20px', textAlign: 'center' }}>
                  <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.75rem', color: 'rgba(238,240,242,0.35)' }}>Har du allerede konto? </span>
                  <button
                  type="button"
                  onClick={() => navigate('/login')}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'Syne, sans-serif', fontWeight: 700, fontSize: '0.75rem', color: '#4A90E2', letterSpacing: '0.04em', textDecoration: 'underline', textDecorationColor: 'rgba(74,144,226,0.4)', padding: isMobile ? '8px 4px' : '0', WebkitTapHighlightColor: 'transparent' }}
                  className="nav-spring">
                    Logg inn
                  </button>
                </div>
              </form>) : (

            /* ── STEP 2: Password ── */
            <form onSubmit={handleSubmit} noValidate className="step-slide-in">
                <div style={{ marginBottom: '16px' }}>
                  <label style={labelStyle}>Passord *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e?.target?.value)}
                    onFocus={() => setFocusedField('password')}
                    onBlur={() => {handleBlur('password');setFocusedField(null);}}
                    placeholder="Minst 8 tegn"
                    autoComplete="new-password"
                    style={{ ...inputStyle('password'), paddingRight: '44px' }} />
                  
                    {touched?.['password'] &&
                  <div style={{ position: 'absolute', right: '36px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        <Icon name={getFieldError('password') ? 'XCircle' : 'CheckCircle'} size={14} color={getFieldError('password') ? '#E85555' : '#4CAF50'} />
                      </div>
                  }
                    <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: isMobile ? '8px' : '4px', WebkitTapHighlightColor: 'transparent' }}>
                      <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={15} color="rgba(46,196,160,0.6)" />
                    </button>
                  </div>
                  <FieldError field="password" />

                  {/* Password strength */}
                  {password &&
                <div style={{ marginTop: '10px' }}>
                      <div style={{ display: 'flex', gap: '4px', marginBottom: '6px' }}>
                        {[1, 2, 3, 4]?.map((bar) =>
                    <div key={bar} style={{
                      flex: 1, height: '3px',
                      background: bar <= pwdStrength?.bars ? pwdStrength?.color : 'rgba(255,255,255,0.08)',
                      transition: 'background 300ms ease'
                    }} />
                    )}
                      </div>
                      {pwdStrength?.label &&
                  <span style={{ fontFamily: 'Syne, sans-serif', fontSize: '0.62rem', color: pwdStrength?.color, fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase' }}>{pwdStrength?.label}</span>
                  }
                    </div>
                }

                  {/* Requirements */}
                  {password &&
                <div style={{ marginTop: '10px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      {pwdRequirements?.map((req) =>
                  <div key={req?.label} style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Icon name={req?.met ? 'CheckCircle' : 'Circle'} size={10} color={req?.met ? '#4CAF50' : 'rgba(238,240,242,0.2)'} />
                          <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.68rem', color: req?.met ? 'rgba(238,240,242,0.6)' : 'rgba(238,240,242,0.25)' }}>{req?.label}</span>
                        </div>
                  )}
                    </div>
                }
                </div>
                <div style={{ marginBottom: '24px' }}>
                  <label style={labelStyle}>Bekreft passord *</label>
                  <div style={{ position: 'relative' }}>
                    <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e?.target?.value)}
                    onFocus={() => setFocusedField('confirmPassword')}
                    onBlur={() => {handleBlur('confirmPassword');setFocusedField(null);}}
                    placeholder="Gjenta passordet"
                    autoComplete="new-password"
                    style={{ ...inputStyle('confirmPassword'), paddingRight: '44px' }} />
                  
                    {touched?.['confirmPassword'] &&
                  <div style={{ position: 'absolute', right: '36px', top: '50%', transform: 'translateY(-50%)', pointerEvents: 'none' }}>
                        <Icon name={getFieldError('confirmPassword') ? 'XCircle' : 'CheckCircle'} size={14} color={getFieldError('confirmPassword') ? '#E85555' : '#4CAF50'} />
                      </div>
                  }
                    <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: isMobile ? '8px' : '4px', WebkitTapHighlightColor: 'transparent' }}>
                      <Icon name={showConfirmPassword ? 'EyeOff' : 'Eye'} size={15} color="rgba(46,196,160,0.6)" />
                    </button>
                  </div>
                  <FieldError field="confirmPassword" />
                </div>
                {error &&
              <div style={{ background: 'rgba(232,85,85,0.08)', border: '1px solid rgba(232,85,85,0.25)', borderLeft: '3px solid #E85555', padding: '10px 14px', marginBottom: '18px', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Icon name="AlertCircle" size={13} color="#E85555" />
                    <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.8rem', color: '#E85555' }}>{error}</span>
                  </div>
              }
                <div style={{ display: 'flex', gap: '10px' }}>
                  <button
                  type="button"
                  onClick={() => setStep(1)}
                  style={{
                    flex: '0 0 auto',
                    padding: isMobile ? '16px 16px' : '15px 16px',
                    background: 'rgba(255,255,255,0.06)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    color: 'rgba(238,240,242,0.6)',
                    fontFamily: 'Syne, sans-serif',
                    fontSize: '0.8rem',
                    fontWeight: 700,
                    cursor: 'pointer',
                    display: 'flex', alignItems: 'center', gap: '6px',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation'
                  }}>
                    <Icon name="ArrowLeft" size={14} color="rgba(238,240,242,0.6)" />
                  </button>
                  <button
                  type="submit"
                  disabled={isLoading}
                  style={{
                    flex: 1,
                    padding: isMobile ? '16px 24px' : '15px 24px',
                    background: isLoading ? 'rgba(46,196,160,0.45)' : '#2EC4A0',
                    border: 'none',
                    color: '#1A1F28',
                    fontFamily: 'Syne, sans-serif',
                    fontSize: isMobile ? '0.9375rem' : '0.875rem',
                    fontWeight: 800,
                    letterSpacing: '0.1em',
                    textTransform: 'uppercase',
                    cursor: isLoading ? 'not-allowed' : 'pointer',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px',
                    boxShadow: isLoading ? 'none' : '0 4px 24px rgba(46,196,160,0.35)',
                    transition: 'all 200ms ease',
                    WebkitTapHighlightColor: 'transparent',
                    touchAction: 'manipulation'
                  }}
                  className={isLoading ? '' : 'btn-spring'}>
                    <Icon name={isLoading ? 'Loader2' : 'UserPlus'} size={15} color="#1A1F28" />
                    {isLoading ? 'Oppretter...' : 'Opprett konto'}
                  </button>
                </div>
              </form>)
            }

            {/* Card footer */}
            <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.6rem', color: 'rgba(238,240,242,0.2)', letterSpacing: '0.05em' }}>nordpass.no</span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                <div style={{ width: '6px', height: '6px', background: '#2EC4A0', borderRadius: '50%' }} />
                <div style={{ width: '6px', height: '6px', background: '#F5A623', borderRadius: '50%' }} />
              </div>
              <span style={{ fontFamily: 'Georgia, serif', fontStyle: 'italic', fontSize: '0.6rem', color: 'rgba(238,240,242,0.2)', letterSpacing: '0.05em' }}>© 2026</span>
            </div>
          </div>
        </div>
      </div>
    </>);

};

export default RegisterPage;